///
/// EarthBrowser
///
/// Created by Matt Giger on 1/10/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type RenderState } from "@stovokor/engine/RenderState"
import { clamp } from "@stovokor/math/math"
import { time, timedelta, timerange } from "@stovokor/math/time"
import { type Texture } from "@stovokor/shader/Texture"
import { type EarthBrowserContext } from "../EarthBrowserContext"

const cTimeUpdateInterval = 1000 // one second in milliseconds

/**
 * Represents a single time frame in a tile series.
 * Each frame has a URL template that includes {z}/{x}/{y} placeholders.
 */
export class TileFrame {
	public urlTemplate: string
	public range: timerange
	public endOffset = timedelta.hours(0)

	constructor(urlTemplate: string, startTime: time, duration: timedelta) {
		this.urlTemplate = urlTemplate
		this.range = new timerange(startTime, startTime.adding(duration))
	}

	public inRange(t: time): boolean {
		return this.range.start.lessOrEqual(t) && this.range.end.adding(this.endOffset).greaterOrEqual(t)
	}
}

/**
 * Manages a series of tile frames over time.
 * Provides current and next frame URL templates plus blend factor for smooth time interpolation.
 */
export class TileSeries {
	/** Blend factor between current (0) and next (1) frame */
	public blendFactor: number = 0

	/** Current frame being displayed */
	public currentFrame?: TileFrame

	/** Next frame for blending */
	public nextFrame?: TileFrame

	/** Optional frame-to-frame optical flow for the current/next pair, as an
	 *  RG displacement in global equirect UV (the field the mosaic overlay
	 *  estimates, PrecipFlowTextureSeries). Tiles warp their frames along it
	 *  while blending; undefined falls back to an in-place cross-dissolve */
	public flowTexture: () => Texture | undefined = () => undefined

	/** Overall opacity of the tiles (0..1): the owner lowers it when another
	 *  layer takes over the picture (the forecast radar past the newest
	 *  observation), matching the fade of the equirect overlay they refine */
	public fade: () => number = () => 1

	/** Time range covered by all frames */
	protected range = new timerange(time.longAgo, time.longAgo)

	/** Offset added to last frame's end time */
	protected endOffset: timedelta = timedelta.hours(1)

	/** All frames in chronological order */
	protected frames: TileFrame[] = []

	/** How often to check for frame updates (ms) */
	protected updateInterval: number = cTimeUpdateInterval

	/** Last time we updated frame state */
	protected lastUpdateTime: number = 0

	/** Version number that increments when frames change */
	public version: number = 0

	private context: EarthBrowserContext

	constructor(context: EarthBrowserContext) {
		this.context = context
	}

	public clear(): void {
		this.frames = []
		this.currentFrame = undefined
		this.nextFrame = undefined
		this.range = new timerange(time.longAgo, time.longAgo)
		this.lastUpdateTime = 0
		this.blendFactor = 0
		this.version++
	}

	/**
	 * Set new frames. Frames should be in chronological order.
	 */
	public setFrames(frames: TileFrame[]): void {
		this.currentFrame = undefined
		this.nextFrame = undefined
		this.lastUpdateTime = 0
		this.blendFactor = 0

		// Sort by start time
		this.frames = [...frames].sort((a, b) => a.range.start.julian - b.range.start.julian)

		const first = this.frames[0]
		const last = this.frames[this.frames.length - 1]
		if (first && last) {
			this.range.start = first.range.start
			this.range.end = last.range.end.adding(this.endOffset)
			last.endOffset = this.endOffset
		} else {
			this.range = new timerange()
		}

		this.version++
	}

	/**
	 * Get the URL template for the current frame, or undefined if no current frame.
	 */
	public get currentUrlTemplate(): string | undefined {
		return this.currentFrame?.urlTemplate
	}

	/**
	 * Get the URL template for the next frame, or undefined if no next frame.
	 */
	public get nextUrlTemplate(): string | undefined {
		return this.nextFrame?.urlTemplate
	}

	/**
	 * Update time state. Call this each frame.
	 */
	public updateState(_state: RenderState): void {
		const clockTime = this.context.clock.clockTime

		// Throttle the frame search; the blend below runs every frame so the
		// advected transition glides at 1x playback instead of stepping once
		// per clock second, matching TextureSeries for the mosaic underneath
		if (Math.abs(clockTime.date.getTime() - this.lastUpdateTime) >= this.updateInterval) {
			this.lastUpdateTime = clockTime.date.getTime()

			// Same selection rule as TextureSeries, so the refinement tiles and
			// the mosaic texture underneath always show the same slot: the
			// latest frame starting at or before the clock. In the gap between
			// two slots that is the earlier one, blending toward the next; past
			// the newest frame it holds the newest (live mode is always a little
			// after the last slot) with no wrap back to the oldest frame; before
			// the first frame there is no data, so no frame is selected.
			const index = this.frameIndexAt(clockTime)
			const current = index >= 0 ? this.frames[index] : undefined
			const next = index >= 0 && index + 1 < this.frames.length ? this.frames[index + 1] : undefined
			if (current !== this.currentFrame || next !== this.nextFrame) {
				this.currentFrame = current
				this.nextFrame = next
				this.version++
			}
		}

		const current = this.currentFrame
		const next = this.nextFrame
		this.blendFactor = 0
		if (current && next) {
			const sceneDiff = clockTime.difference(current.range.start)
			const frameDiff = next.range.start.difference(current.range.start)
			// Guard against duplicate/zero-length frame spacing
			if (frameDiff.jdelta > 0) {
				this.blendFactor = clamp(sceneDiff.jdelta / frameDiff.jdelta, 0, 1)
			}
		}
	}

	/** Latest frame index whose start is at or before the given time (-1 if none) */
	private frameIndexAt(t: time): number {
		for (let index = this.frames.length - 1; index >= 0; --index) {
			if (this.frames[index].range.start.lessOrEqual(t)) {
				return index
			}
		}
		return -1
	}

	/**
	 * Check if we have valid frames
	 */
	public get hasFrames(): boolean {
		return this.frames.length > 0
	}

	/** Time span covered by the current frame list (undefined when empty) */
	public get timeRange(): timerange | undefined {
		return this.frames.length > 0 ? this.range : undefined
	}

	/**
	 * Get all frames
	 */
	public get allFrames(): TileFrame[] {
		return this.frames
	}
}
